import { fetchAuthSession, signOut as amplifySignOut } from 'aws-amplify/auth';
import type { JWT } from 'aws-amplify/auth';
import { ReactNode, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import CustomSignIn from './CustomSignIn';
import { AuthState } from '../../libs';

interface AuthGateProps {
  children: (_: { user: JWT['payload']; signOut: () => void }) => ReactNode;
}

export default function AuthGate({ children }: AuthGateProps) {
  const [authState, setAuthState] = useState(AuthState.loading);
  const [claims, setClaims] = useState<JWT['payload'] | null>(null);

  useEffect(() => {
    void checkAuthState();
  }, []);

  const checkAuthState = async () => {
    let payload: JWT['payload'] | undefined;
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      payload = session.tokens?.idToken?.payload;
    } catch {
      try {
        // A forced refresh fails on a connectivity blip while the stored tokens are still valid.
        const session = await fetchAuthSession();
        payload = session.tokens?.idToken?.payload;
      } catch {
        payload = undefined;
      }
    }

    if (payload) {
      setClaims(payload);
      setAuthState(AuthState.signedIn);
    } else {
      setAuthState(AuthState.signIn);
    }
  };

  const signOut = async () => {
    try {
      await amplifySignOut();
      setAuthState(AuthState.signIn);
    } catch {
      toast.error('An error occured while logging you out please try again');
    }
  };

  switch (authState) {
    case AuthState.signedIn: {
      return <>{children({ user: claims!, signOut: () => void signOut() })}</>;
    }
    case AuthState.signIn: {
      return <CustomSignIn onSignedIn={() => void checkAuthState()} />;
    }
    default: {
      return <></>;
    }
  }
}
